import React from 'react';
import { ArrowLeft, Database, FileText, Search, TrendingUp, BarChart3, PieChart, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';

const SemanticLakehouse = () => {
  const features = [
    {
      icon: FileText,
      title: "Recursive Text Splitting",
      description: "Advanced text segmentation that preserves context while breaking down large documents into manageable chunks."
    },
    {
      icon: Search,
      title: "Semantic Chunking",
      description: "AI-powered content analysis that groups related information based on financial ontologies (GAAP, IFRS)."
    },
    {
      icon: TrendingUp,
      title: "Vector Engine Integration",
      description: "Sub-second retrieval with Pinecone, Milvus, or Weaviate for instant access to relevant financial data."
    },
    {
      icon: BarChart3,
      title: "ETL Intelligence",
      description: "Automated extraction of balance sheets, cash flow statements, and risk disclosures into structured JSON-LD."
    }
  ];

  const dataTypes = [
    {
      name: "Financial Statements",
      icon: PieChart,
      description: "Balance sheets, income statements, cash flow statements automatically extracted and structured.",
      color: "from-blue-600 to-cyan-600"
    },
    {
      name: "Regulatory Filings",
      icon: FileText,
      description: "SEC filings, annual reports, and compliance documents indexed for instant retrieval.",
      color: "from-green-600 to-emerald-600"
    },
    {
      name: "Market Data",
      icon: Activity,
      description: "Real-time and historical market data integrated with semantic understanding.",
      color: "from-purple-600 to-pink-600"
    },
    {
      name: "Risk Reports",
      icon: TrendingUp,
      description: "Risk assessments, VaR calculations, and exposure analysis automatically categorized.",
      color: "from-orange-600 to-red-600"
    }
  ];

  const capabilities = [
    "80% reduction in data processing time",
    "99.5% accuracy in financial document extraction",
    "Sub-second query response times",
    "Support for 50+ document formats",
    "Real-time data synchronization",
    "Automated quality validation"
  ];

  return (
    <div className="pt-16 min-h-screen bg-navy-950 text-white">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <div>
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-r from-[#f97316] to-[#fbce16] mb-6">
                <Database className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-5">
                <span className="text-gradient-blue">Financial Semantic Lakehouse</span>
              </h1>
              <h2 className="text-xl sm:text-2xl text-slate-300 mb-6">Turning "Dark Data" into Liquid Assets</h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                80% of financial data is trapped in unstructured PDFs, emails, and legacy Excel sheets.
                Our AI-native data lakes transform this dark data into actionable insights.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#demo"
                  className="inline-flex items-center justify-center px-7 py-3 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-[1.02]"
                >
                  Request Demo
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-7 py-3 border border-slate-700/50 text-white font-semibold rounded-lg hover:bg-white/5 transition-all duration-300"
                >
                  View All Solutions
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#f97316]/20 to-[#fbce16]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/40 shadow-2xl shadow-orange-900/20">
                <img
                  src="/solution3_AI_datalakehouse.png"
                  alt="Financial semantic lakehouse"
                  className="w-full h-auto object-cover max-h-[340px] sm:max-h-[420px] lg:max-h-[520px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/45 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-white">The Challenge</h3>
              <p className="text-lg text-slate-300 leading-relaxed">
                Financial institutions generate massive amounts of unstructured data daily. 
                Critical insights remain hidden in PDFs, emails, spreadsheets, and legacy systems, 
                making it impossible to leverage this information for real-time decision-making.
              </p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-3xl font-bold text-red-400">80%</p>
                  <p className="text-slate-300">Data is unstructured</p>
                </div>
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-3xl font-bold text-orange-400">60%</p>
                  <p className="text-slate-300">Time spent on data preparation</p>
                </div>
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-3xl font-bold text-yellow-400">$2.5M</p>
                  <p className="text-slate-300">Average annual data loss</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="flex items-start space-x-4">
              <Database className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">The BeeMind Solution</h3>
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                  We architect AI-Native Data Lakes that go beyond simple storage. Our pipeline uses 
                  Recursive Character Text Splitting and Semantic Chunking to index data according to 
                  financial ontologies (GAAP, IFRS).
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                    <p className="text-lg font-semibold text-green-400 mb-2">Vector Engine</p>
                    <p className="text-slate-300">Integration with Pinecone, Milvus, or Weaviate for sub-second retrieval.</p>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                    <p className="text-lg font-semibold text-green-400 mb-2">ETL Intelligence</p>
                    <p className="text-slate-300">Automated extraction of balance sheets, cash flow statements, and risk disclosures into structured JSON-LD.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Core Features</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 premium-border premium-elevate hover:border-green-500 transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="inline-flex p-3 rounded-lg bg-gradient-to-r from-green-600 to-teal-600">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold mb-2 text-white">{feature.title}</h4>
                        <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Supported Data Types</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {dataTypes.map((type, index) => {
                const Icon = type.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 hover:border-green-500 transition-all duration-300">
                    <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${type.color} mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold mb-2 text-white">{type.name}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{type.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Performance Capabilities</h3>
            <div className="bg-navy-800 rounded-xl p-8 border border-slate-700/40">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-slate-300">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold mb-8">Transform Your Data Strategy</h3>
            <p className="text-xl text-slate-400 mb-8">
              Unlock the full potential of your financial data with our AI-native semantic lakehouse.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#demo"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-105"
              >
                Schedule Demo
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 border border-slate-700/40 text-white font-semibold rounded-lg hover:bg-navy-800 transition-all duration-300"
              >
                View Other Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SemanticLakehouse;
